import { apiClient } from './api';
import { decodeJWT, isTokenExpired } from '../utils/jwtHelper';

const AMINPAY_API_URL =
  import.meta.env.VITE_AMINPAY_API_URL || 'http://localhost:8000';

export interface AminIdLoginCredentials {
  email: string;
  mpin: string;
}

export interface AminIdCredentialLoginResponse {
  requires2fa: boolean;
  challengeToken?: string;
  allowedFactors?: string[];
  tokens?: { accessToken?: string; refreshToken?: string };
}

export interface AminIdVerifyTwoFactorPayload {
  challengeToken: string;
  factorType: string;
  code: string;
}

export interface AminIdSetMpinPayload {
  newMpin: string;
  currentPassword?: string;
  currentMpin?: string;
}

const getAuthErrorMessage = (error: unknown, fallback: string): string => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: string | string[] } } })
      .response?.data?.message !== 'undefined'
  ) {
    const message = (error as { response?: { data?: { message?: string | string[] } } })
      .response?.data?.message;

    if (Array.isArray(message)) {
      return message.join(', ');
    }

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

export const authService = {
  async loginWithCredentials(
    credentials: AminIdLoginCredentials,
  ): Promise<AminIdCredentialLoginResponse> {
    const response = await fetch(`${AMINPAY_API_URL}/aminid/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        message?: string | string[];
      } | null;

      if (Array.isArray(payload?.message)) {
        throw new Error(payload.message.join(', '));
      }

      throw new Error(
        payload?.message || 'Invalid credentials or AminID access not granted',
      );
    }

    return (await response.json()) as AminIdCredentialLoginResponse;
  },

  async verifySecondFactor(
    payload: AminIdVerifyTwoFactorPayload,
  ): Promise<{ accessToken: string }> {
    const response = await fetch(`${AMINPAY_API_URL}/aminid/auth/2fa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorPayload = (await response.json().catch(() => null)) as {
        message?: string | string[];
      } | null;

      if (Array.isArray(errorPayload?.message)) {
        throw new Error(errorPayload.message.join(', '));
      }

      throw new Error(
        errorPayload?.message || 'Unable to verify second factor',
      );
    }

    const responsePayload = (await response.json()) as {
      tokens?: { accessToken?: string };
    };
    const accessToken = responsePayload.tokens?.accessToken;

    if (!accessToken) {
      throw new Error('AminID second-factor verification failed');
    }

    return { accessToken };
  },

  async setMpin(payload: AminIdSetMpinPayload): Promise<void> {
    try {
      await apiClient.patch('/auth/mpin', payload);
    } catch (error) {
      throw new Error(
        getAuthErrorMessage(error, 'Unable to update MPIN at this time'),
      );
    }
  },

  async validateToken(token: string) {
    if (isTokenExpired(token)) {
      throw new Error('Token has expired');
    }

    const decoded = decodeJWT(token);
    if (!decoded) {
      throw new Error('Invalid token');
    }

    try {
      await apiClient.get('/customer-identity/list', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { limit: 1 },
      });
    } catch (error) {
      throw new Error('Token validation failed');
    }

    return {
      id: decoded.userId || parseInt(decoded.sub) || 1,
      email: decoded.email || 'admin@aminpay.net',
      name: decoded.name || 'Admin User',
    };
  },
};
